import {useMemo} from 'react'
import {GearIcon} from '@primer/octicons-react'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import type {Assignee as PageDataAssignee} from '@github-ui/dotcom-schema/types/pull_requests/v1/assignees'
import {AssigneePicker, type AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import {SidebarDisclosureHeader} from './SidebarDisclosureHeader'
import {useAssigneesQuery} from './data/route-queries'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useModifyAssignees} from '../../mutations/use-modify-assignees'
import {useOverviewRoute} from './data/use-overview-route'

/**
 * "Manage assignees" header for the Overview sidebar. Anchors the
 * shared assignee picker (`@github-ui/item-picker-assignee-tsq`) to the section
 * header, mirroring the read-only list body below it.
 */
export function AssigneesPicker() {
  const {data: assigneesData, isPending, isError} = useAssigneesQuery()
  const {owner, repo, pr_number: prNumber} = useRouteParams(useOverviewRoute())
  const number = Number(prNumber)
  const {mutate} = useModifyAssignees()

  // Memoized so the picker receives stable array identities. `assignees` drives the
  // picker's item ordering and resets its live selection, so a fresh array on every
  // render would re-sort the open list and clobber in-progress selections.
  const selectedAssignees = useMemo(
    () => (assigneesData?.assignees ?? []).map(toPickerAssignee),
    [assigneesData?.assignees],
  )

  if (isPending && !assigneesData) return <AssigneesHeader disabled aria-label="Loading assignees" />

  if (isError && !assigneesData) {
    return <AssigneesHeader disabled aria-label="Assignees couldn't be loaded" />
  }

  if (owner === undefined || repo === undefined) return null

  const originalNodeIds = new Set(selectedAssignees.map(a => a.id))

  return (
    <AssigneePicker
      owner={owner}
      repo={repo}
      number={number}
      assignees={selectedAssignees}
      viewer={null}
      selectionVariant="multiple"
      title="Assign up to 10 people to this pull request"
      onSelectionChange={next => {
        // Diff on relay node id (the id space the picker and the mutation share).
        const nextNodeIds = new Set(next.map(a => a.id))

        const addAssignees = next.filter(a => !originalNodeIds.has(a.id)).map(a => a.id)
        const removeAssignees = selectedAssignees.filter(a => !nextNodeIds.has(a.id)).map(a => a.id)

        if (addAssignees.length === 0 && removeAssignees.length === 0) return
        mutate({addAssignees, removeAssignees, nextAssignees: next})
      }}
      anchorElement={(anchorProps, ref) => <AssigneesHeader ref={ref} aria-haspopup="dialog" {...anchorProps} />}
    />
  )
}

function toPickerAssignee(assignee: PageDataAssignee): AssigneePickerAssignee {
  const copilot = isCopilot(assignee.displayLogin)
  // Page-data marks bots/integrations with a non-empty `slug`.
  const bot = copilot || Boolean(assignee.slug)

  return {
    id: assignee.nodeId,
    databaseId: assignee.id <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(assignee.id) : null,
    login: assignee.displayLogin,
    name: null,
    avatarUrl: assignee.avatarUrl,
    __typename: bot ? 'Bot' : 'User',
    isCopilot: copilot,
    isAgent: copilot,
  }
}

function AssigneesHeader(props: React.ComponentPropsWithRef<'button'>) {
  return <SidebarDisclosureHeader title="Assignees" trailingIcon={GearIcon} aria-label="Manage assignees" {...props} />
}
